import * as ms from 'milliseconds';
import React from 'react';
import { StyleProp, ViewStyle } from 'react-native';
import { MBAnimation } from '@shared/components/animation';

interface MBBlinkingBackgroundProps {
  from?: string;
  to: string;
  style?: StyleProp<ViewStyle>;
  children: React.ReactNode;
}

// Math.cos(t)                              => cosine wave (x: 0 => 6.24, y: -1   => 1)
// Math.cos(t * 2 * Math.PI)                => cosine wave (x: 0 => 1,    y: -1   => 1)
// Math.cos(t * 2 * Math.PI) / 2            => cosine wave (x: 0 => 1,    y: -0.5 => 0.5)
// Math.cos(t * 2 * Math.PI) / 2 + 0.5      => cosine wave (x: 0 => 1,    y:  0   => 1)
// Math.cos(t * 2 * Math.PI * 2) / 2 + 0.5  => repeat the loop 2x
// To visualize this, search for this on Google:
// y=cos(x*pi*4)/2+0.5
function blinkEasing(t: number) {
  const waitRatio = 0.5;
  // wait for X% of the animation
  if (t < waitRatio) {
    return 1;
  }
  // u: the normalized progress between (waitRatio) => 1
  // If waitRatio is 0.5, we first wayt 50% of the animation.
  // At t=0.75, u would be 50% (50% from 0.5 -> 1.0)
  const u = (t - waitRatio) / (1 - waitRatio);
  // then go to the secondary color and back
  return Math.cos(u * 2 * Math.PI * 1.5) / 2 + 0.5;
}

/*
    This component changes background colors between from and to.
    If "from" is not set, it will only show "to"
*/
export function MBBlinkingBackground({ from, to, style, children }: MBBlinkingBackgroundProps) {
  return (
    <MBAnimation
      disabled={!from}
      duration={ms.seconds(3)}
      easing={blinkEasing}
      backgroundColor={!!from ? { from, to } : { to, from: to }}
      style={[style, { backgroundColor: to }]}
    >
      {children}
    </MBAnimation>
  );
}

interface MBBlinkingOpacityProps {
  disabled: boolean;
  style?: StyleProp<ViewStyle>;
  children: React.ReactNode;
}

export function MBBlinkingOpacity({ disabled, style, children }: MBBlinkingOpacityProps) {
  return (
    <MBAnimation
      disabled={disabled}
      opacity={{ from: 1, to: 0.3 }}
      duration={ms.seconds(3)}
      easing={blinkEasing}
      style={style}
    >
      {children}
    </MBAnimation>
  );
}
